import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ADJUSTMENT_TYPES = {
  damage: { label: "Damage", color: "bg-red-100 text-red-800" },
  loss: { label: "Loss", color: "bg-red-100 text-red-800" },
  correction: { label: "Correction", color: "bg-blue-100 text-blue-800" },
  write_off: { label: "Write Off", color: "bg-gray-100 text-gray-800" },
  found: { label: "Found", color: "bg-green-100 text-green-800" },
  return: { label: "Return", color: "bg-indigo-100 text-indigo-800" },
};

const ADJUSTMENT_STATUSES = {
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
  approved: { label: "Approved", color: "bg-green-100 text-green-800" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-800" },
};

const ViewAdjustment = () => {
  const { token } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [adjustment, setAdjustment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    fetchAdjustment();
  }, [id]);

  const fetchAdjustment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getStockAdjustment/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setAdjustment(data.adjustment || data);
      } else {
        setErrorMessage(data.error || "Failed to fetch adjustment");
      }
    } catch (err) {
      setErrorMessage("Failed to fetch stock adjustment");
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const config = ADJUSTMENT_TYPES[type] || { label: type, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const config = ADJUSTMENT_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!adjustment) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Stock adjustment not found</p>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/Adjustments")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {adjustment.adjustmentNumber || `ADJ-${adjustment._id?.slice(-6).toUpperCase()}`}
            </h2>
            <p className="text-sm text-gray-500">
              Created {adjustment.date
                ? new Date(adjustment.date).toLocaleDateString()
                : adjustment.createdAt
                ? new Date(adjustment.createdAt).toLocaleDateString()
                : "N/A"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getTypeBadge(adjustment.adjustmentType)}
          {getStatusBadge(adjustment.status)}
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Adjustment Info */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Adjustment Details</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Adjustment Number</p>
                <p className="font-medium">
                  {adjustment.adjustmentNumber || adjustment._id?.slice(-6).toUpperCase()}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Type</p>
                <p className="font-medium capitalize">
                  {ADJUSTMENT_TYPES[adjustment.adjustmentType]?.label || adjustment.adjustmentType}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium capitalize">
                  {ADJUSTMENT_STATUSES[adjustment.status]?.label || adjustment.status}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Date</p>
                <p className="font-medium">
                  {adjustment.date
                    ? new Date(adjustment.date).toLocaleDateString()
                    : adjustment.createdAt
                    ? new Date(adjustment.createdAt).toLocaleDateString()
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Created By</p>
                <p className="font-medium">
                  {adjustment.createdBy?.name || adjustment.createdBy?.email || "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Item Details */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Item Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Item Name</p>
                <p className="font-medium">{adjustment.item?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">SKU</p>
                <p className="font-medium">{adjustment.item?.sku || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Category</p>
                <p className="font-medium">{adjustment.item?.category?.name || "N/A"}</p>
              </div>
            </div>
          </div>

          {/* Quantity Change */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Stock Change</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500 uppercase mb-1">Previous Qty</p>
                <p className="text-2xl font-semibold text-gray-700">
                  {adjustment.previousQuantity ?? "-"}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500 uppercase mb-1">Change</p>
                <p className={`text-2xl font-semibold ${
                  adjustment.quantityChange > 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {adjustment.quantityChange > 0 ? "+" : ""}{adjustment.quantityChange ?? "-"}
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <p className="text-xs text-gray-500 uppercase mb-1">New Qty</p>
                <p className="text-2xl font-semibold text-primary">
                  {adjustment.newQuantity ?? "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Reason */}
          {adjustment.reason && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Reason</h3>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{adjustment.reason}</p>
            </div>
          )}
        </div>

        {/* Right: Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="font-medium capitalize">
                  {ADJUSTMENT_TYPES[adjustment.adjustmentType]?.label || adjustment.adjustmentType}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <span className="font-medium capitalize">
                  {ADJUSTMENT_STATUSES[adjustment.status]?.label || adjustment.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Quantity Changed</span>
                <span className={`font-medium ${
                  adjustment.quantityChange > 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {adjustment.quantityChange > 0 ? "+" : ""}{adjustment.quantityChange}
                </span>
              </div>
              {adjustment.item?.unitCost && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Value Impact</span>
                  <span className="font-medium">
                    Nu. {(Math.abs(adjustment.quantityChange || 0) * adjustment.item.unitCost).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Timestamps */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Timestamps</h3>
            <div className="space-y-3 text-sm">
              {adjustment.createdAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Created</span>
                  <span className="font-medium">
                    {new Date(adjustment.createdAt).toLocaleString()}
                  </span>
                </div>
              )}
              {adjustment.updatedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Updated</span>
                  <span className="font-medium">
                    {new Date(adjustment.updatedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAdjustment;
